import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import type { Message, ReferenceCard } from "../../../../types";
import {
  getReferenceCardFullAnswer,
  getReferenceCardShortAnswer,
  getReferenceCards,
} from "../../../../helpers/referenceCard";
import { useClampedGalleryIndex } from "../../../../hooks/useClampedGalleryIndex";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { useReferenceCardMessageStyles } from "./styles";

type ReferenceCardMessageProps = {
  message: Message;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
};

const SWIPE_THRESHOLD_PX = 48;

function ReferenceCardBody({
  card,
  cardIndex,
  expanded,
  onToggleExpand,
}: {
  card: ReferenceCard;
  cardIndex: number;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const { styles } = useReferenceCardMessageStyles(cardIndex);
  const shortAnswer = getReferenceCardShortAnswer(card);
  const fullAnswer = getReferenceCardFullAnswer(card);
  const bodyHtml = expanded && fullAnswer ? fullAnswer : shortAnswer;
  const canExpand = Boolean(fullAnswer && fullAnswer !== shortAnswer);
  const category = card.category?.trim() || "";
  const eyebrow = category
    ? `Reference card • ${category}`
    : "Reference card";
  const sourceIssue =
    card.source_issue &&
    typeof card.source_issue === "object" &&
    card.source_issue.severity === "red"
      ? card.source_issue
      : null;

  return (
    <Box sx={styles.card}>
      <Box sx={styles.headerRow}>
        <Typography component="div" sx={styles.eyebrow}>
          {eyebrow}
        </Typography>
        <PushPinOutlinedIcon sx={styles.pinIcon} aria-hidden />
      </Box>

      <Typography component="h3" sx={styles.question}>
        {card.question}
      </Typography>

      {sourceIssue && (
        <Box sx={styles.sourceIssue} role="alert">
          <WarningAmberRoundedIcon sx={styles.sourceIssueIcon} aria-hidden />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={styles.sourceIssueTitle}>Source issue</Typography>
            <Typography sx={styles.sourceIssueMessage}>
              {sourceIssue.message}
            </Typography>
          </Box>
        </Box>
      )}

      {bodyHtml ? (
        <Box sx={styles.body}>
          <CustomMarkdown children={bodyHtml} />
        </Box>
      ) : null}

      {expanded && card.review_status?.trim() ? (
        <Typography sx={styles.reviewStatus}>{card.review_status}</Typography>
      ) : null}

      {canExpand && (
        <Box
          sx={styles.readMoreRow}
          onClick={onToggleExpand}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggleExpand();
            }
          }}
        >
          <Typography sx={styles.readMoreLabel}>
            {expanded ? "Show less" : "Read more"}
          </Typography>
          <ChevronRightIcon
            sx={{
              color: "inherit",
              fontSize: 20,
              transition: "transform 0.2s ease",
              transform: expanded ? "rotate(90deg)" : "none",
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export function ReferenceCardMessage({
  message,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
}: ReferenceCardMessageProps) {
  const cards = useMemo(() => getReferenceCards(message), [message]);
  const { safeIndex, setCurrentImageIndex } = useClampedGalleryIndex(
    cards.length,
  );
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>(
    {},
  );
  const touchStartX = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const { styles: shellStyles, cardBg } =
    useReferenceCardMessageStyles(safeIndex);

  const activeCard = cards[safeIndex];
  const isExpanded = activeCard
    ? Boolean(expandedById[activeCard.id])
    : false;

  const goTo = useCallback(
    (next: number) => {
      if (cards.length <= 1) return;
      setCurrentImageIndex((next + cards.length) % cards.length);
    },
    [cards.length, setCurrentImageIndex],
  );

  const toggleExpand = useCallback(() => {
    if (!activeCard || suppressClickRef.current) return;
    setExpandedById((prev) => ({
      ...prev,
      [activeCard.id]: !prev[activeCard.id],
    }));
  }, [activeCard]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
    suppressClickRef.current = false;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    if (startX == null || cards.length <= 1) return;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    suppressClickRef.current = true;
    goTo(safeIndex + (delta < 0 ? 1 : -1));
  };

  if (!activeCard) return null;

  return (
    <Box sx={shellStyles.root}>
      <Box
        sx={shellStyles.swipeArea}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeCard.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
          >
            <ReferenceCardBody
              card={activeCard}
              cardIndex={safeIndex}
              expanded={isExpanded}
              onToggleExpand={toggleExpand}
            />
          </motion.div>
        </AnimatePresence>
      </Box>

      {cards.length > 1 && (
        <Box sx={shellStyles.galleryNav} aria-label="Reference card pages">
          <IconButton
            size="small"
            aria-label="Previous reference card"
            onClick={() => goTo(safeIndex - 1)}
            sx={shellStyles.galleryNavButton}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Typography sx={shellStyles.galleryCounter}>
            {safeIndex + 1} / {cards.length}
          </Typography>
          <IconButton
            size="small"
            aria-label="Next reference card"
            onClick={() => goTo(safeIndex + 1)}
            sx={shellStyles.galleryNavButton}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={shellStyles.footerRow}>
        <ReactionAndFavorite
          currentReaction={currentReaction}
          markedIds={markedIds}
          isSaved={isSaved}
          isTags={isTags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
          reactionBg={cardBg}
        />
        <Typography variant="caption" sx={shellStyles.timestamp}>
          <span>{format(new Date(message.date), "dd/MM/yy")}</span>
          <span style={{ margin: "0 5px" }} />
          <span>{format(new Date(message.date), "HH:mm")}</span>
        </Typography>
      </Box>
    </Box>
  );
}
